import * as React from 'react'

import {
  CheckIcon,
  ChevronDownIcon,
  ChevronsDownUpIcon,
  ChevronsUpDownIcon,
  CodeIcon,
  MessagesSquareIcon,
  PaperclipIcon,
  SlidersHorizontalIcon,
  TagIcon,
  XIcon,
} from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { AutoSizer, List } from 'react-virtualized'

import {
  IPermitType,
  IWorkflowStep,
  PermitFieldType,
} from '~/client/graph/types.generated'
import PermitFieldIcon from '~/client/src/shared/components/PermitFieldIcon/PermitFieldIcon'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import { getWorkflowStepLevel } from '~/client/src/shared/constants/formStatusesTags'
import { getWorkflowStepDisplayName } from '~/client/src/shared/localization/enumDisplayTexts'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { DASH } from '~/client/src/shared/utils/usefulStrings'

import { IWorkflowField } from './ProcoreIntegrationSection.store'

const SafePermitFieldIcon = ({
  fieldName,
  fieldId,
}: {
  fieldName: string | null
  fieldId?: string
}): JSX.Element => {
  const classNameIcon = 'row permit-field-icon no-grow'

  if (fieldId) {
    if (fieldId.startsWith('special_workflowid')) {
      return (
        <span className={classNameIcon}>
          <TagIcon />
        </span>
      )
    }
    if (fieldId.startsWith('special_workflowlog')) {
      return (
        <span className={classNameIcon}>
          <MessagesSquareIcon />
        </span>
      )
    }
    if (fieldId.startsWith('special_logattachments')) {
      return (
        <span className={classNameIcon}>
          <PaperclipIcon />
        </span>
      )
    }
  }

  if (!fieldName) {
    return <div className={classNameIcon} />
  }

  return <PermitFieldIcon fieldName={fieldName} />
}

const MODAL_HEIGHT = 500
const ROW_HEIGHT = 40
const OVERSCAN_ROW_COUNT = 10
const BOTTOM_PADDING = 16

interface IProps {
  fields: IWorkflowField[]
  selectedFieldIds: string[]
  isMultiple: boolean
  onFieldToggle?: (fieldId: string) => void
  onFieldSelect?: (fieldId: string) => void
  onSelectAll?: () => void
  onDeselectAll?: () => void
  onSelectStepFields?: (stepPosition: number) => void
  onDeselectStepFields?: (stepPosition: number) => void
  title?: string
  procoreFieldName?: string
  allowedFieldTypes?: PermitFieldType[] | null
  allowedSpecialFieldIds?: string[] | null
  fieldFilter?: (field: IWorkflowField) => boolean
  permitType?: IPermitType
  disabled?: boolean
  isOpen?: boolean
  onClose?: () => void
  targetElement?: JSX.Element
}

interface IFieldOptionWithStep {
  field?: IWorkflowField
  isSelected?: boolean
  isStepHeader?: boolean
  stepPosition?: number
  stepName?: string
  fieldCount?: number
}

@observer
export default class StruxHubFieldSelectorModal extends React.Component<IProps> {
  @observable private collapsedSteps: Set<number> = new Set()
  private cachedFieldOptions: IFieldOptionWithStep[] | null = null
  private cachedFilteredFields: IWorkflowField[] | null = null
  private cacheKey: string = ''

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps): void {
    if (
      prevProps.fields !== this.props.fields ||
      prevProps.allowedFieldTypes !== this.props.allowedFieldTypes ||
      prevProps.allowedSpecialFieldIds !== this.props.allowedSpecialFieldIds ||
      prevProps.fieldFilter !== this.props.fieldFilter ||
      prevProps.selectedFieldIds !== this.props.selectedFieldIds
    ) {
      this.cachedFieldOptions = null
      this.cachedFilteredFields = null
      this.cacheKey = ''
    }
  }

  private get safeSelectedFieldIds(): string[] {
    const { selectedFieldIds } = this.props
    return Array.isArray(selectedFieldIds) ? selectedFieldIds : []
  }

  private getCacheKey(): string {
    const { fields, allowedFieldTypes, allowedSpecialFieldIds, fieldFilter } =
      this.props
    return JSON.stringify({
      fieldsLength: fields.length,
      allowedFieldTypes,
      allowedSpecialFieldIds,
      hasFieldFilter: !!fieldFilter,
    })
  }

  public render(): JSX.Element {
    const { title, disabled, isOpen, onClose, targetElement } = this.props
    const displayTitle = title || this.getTitle()

    const target = targetElement || (
      <div className="tw:flex tw:items-center full-height">
        <span className="tw:flex-1 text large">{displayTitle}</span>
        <SlidersHorizontalIcon className="tw:size-4 tw:text-muted-foreground" />
      </div>
    )

    return (
      <Popover
        open={disabled ? false : isOpen}
        onOpenChange={open => !open && onClose?.()}
      >
        <PopoverTrigger
          nativeButton={false}
          disabled={disabled}
          render={<div className="full-height full-width" />}
        >
          {target}
        </PopoverTrigger>
        <PopoverContent
          side="right"
          sideOffset={10}
          className="tw:h-[500px] tw:w-auto tw:max-w-[400px] tw:gap-0 tw:overflow-hidden tw:p-0"
        >
          {this.fieldsList}
        </PopoverContent>
      </Popover>
    )
  }

  private get fieldsList(): JSX.Element {
    const { fields, onClose } = this.props
    const displayText = 'Workflow Fields'

    if (!fields || fields.length === 0) {
      return (
        <div className="col full-width full-height overflow-hidden no-outline-container">
          <div className="tw:flex tw:items-center bb-light-grey px16 py12">
            <XIcon
              className="pointer tw:size-5 tw:text-foreground"
              onClick={onClose}
            />
            <div className="tw:flex-1 text medium bold center flex-grow">
              {displayText}
            </div>
            <div className="w10" />
          </div>
          <div className="pt10 pb20 virtualized-list-smart-wrapper px24">
            <div className="text center large bold py20">
              {Localization.translator.none}
            </div>
          </div>
        </div>
      )
    }

    const fieldOptions = this.getFieldOptions()
    const totalCount = this.getTotalSelectableFieldsCount()
    const selectedCount = this.getSelectedFieldsCount()

    return (
      <div className="col full-width full-height overflow-hidden no-outline-container">
        <div className="tw:flex tw:items-center bb-light-grey px20 py12">
          <XIcon
            className="pointer tw:size-4 tw:text-foreground"
            onClick={onClose}
          />
          <div className="tw:flex-1 row x-center text large bold">
            {displayText}
          </div>
        </div>

        <div
          className={classList({
            'row y-center x-between py8 bb-light-grey': true,
            pl20: this.props.isMultiple,
            pl17: !this.props.isMultiple,
          })}
        >
          <div className="row y-center gap-10">
            {this.props.isMultiple &&
              this.props.onSelectAll &&
              this.props.onDeselectAll &&
              totalCount > 0 && (
                <div
                  className="row x-center y-center no-grow mw14 mh14 ba-grey-scale-light brada2 pointer"
                  onClick={this.handleSelectAllClick}
                >
                  {this.hasAnyFieldSelected() && (
                    <span className="text large bold brand-dark center">
                      {DASH}
                    </span>
                  )}
                </div>
              )}
            {this.hasAnySteps() && (
              <div
                className="tw:flex tw:items-center tw:justify-center no-grow"
                onClick={this.handleToggleAllStepsClick}
              >
                {this.areAllStepsCollapsed() ? (
                  <ChevronsUpDownIcon className="tw:size-4" />
                ) : (
                  <ChevronsDownUpIcon className="tw:size-4" />
                )}
              </div>
            )}
            <span className="text large light">
              {totalCount} {Localization.translator.fields || 'Fields'}
              {` / ${selectedCount} selected`}
            </span>
          </div>
        </div>

        <div className="col pb15 virtualized-list-smart-wrapper">
          {!fieldOptions.length ? (
            <div className="text center large bold py20 px24">
              {Localization.translator.none}
            </div>
          ) : (
            <AutoSizer disableHeight={true}>
              {({ width }) => {
                const headerHeight = 48
                const summaryHeight = 40
                const availableHeight =
                  MODAL_HEIGHT - headerHeight - summaryHeight - BOTTOM_PADDING

                return (
                  <List
                    data={fieldOptions}
                    width={width}
                    height={Math.min(
                      availableHeight,
                      ROW_HEIGHT * fieldOptions.length + BOTTOM_PADDING,
                    )}
                    rowHeight={ROW_HEIGHT}
                    rowCount={fieldOptions.length}
                    rowRenderer={({ key, style, index }) =>
                      this.renderOption({ key, style, index, fieldOptions })
                    }
                    scrollToAlignment="start"
                    overscanRowCount={OVERSCAN_ROW_COUNT}
                  />
                )
              }}
            </AutoSizer>
          )}
        </div>
      </div>
    )
  }

  @action.bound
  private handleSelectAllClick(e: React.MouseEvent): void {
    e.stopPropagation()
    if (this.areAllFieldsSelected()) {
      this.props.onDeselectAll?.()
    } else {
      this.props.onSelectAll?.()
    }
  }

  @action.bound
  private handleToggleAllStepsClick(e: React.MouseEvent): void {
    e.stopPropagation()
    this.toggleAllStepsCollapse()
  }

  private getTotalSelectableFieldsCount(): number {
    const { fields } = this.props
    const filteredFields = this.getFilteredFields()
    const filteredFieldIds = new Set(filteredFields.map(f => f.id))

    return fields.filter(
      field => !field.isDisabled && filteredFieldIds.has(field.id),
    ).length
  }

  private getSelectedFieldsCount(): number {
    return this.safeSelectedFieldIds.length
  }

  private getFilteredFields(): IWorkflowField[] {
    const cacheKey = this.getCacheKey()
    if (this.cachedFilteredFields && this.cacheKey === cacheKey) {
      return this.cachedFilteredFields
    }

    const { fields, allowedFieldTypes, allowedSpecialFieldIds, fieldFilter } =
      this.props

    const passesFilter = (field: IWorkflowField): boolean => {
      if (field.isDisabled) return false

      if (allowedFieldTypes === null && fieldFilter) {
        return fieldFilter(field)
      } else if (
        allowedFieldTypes !== null &&
        allowedFieldTypes !== undefined
      ) {
        if (field.id.startsWith('special_')) {
          if (allowedSpecialFieldIds === null) return true
          if (allowedSpecialFieldIds === undefined) return false
          if (allowedSpecialFieldIds.length === 0) return false
          return allowedSpecialFieldIds.includes(field.id)
        }
        return field.type !== null && allowedFieldTypes.includes(field.type)
      }
      return true
    }

    let filteredFields: IWorkflowField[]

    if (allowedFieldTypes === null && fieldFilter) {
      filteredFields = fields.filter(field => {
        if (field.isDisabled) {
          const hasValidColumn = fields.some(
            f =>
              f.parentFieldId === field.id && !f.isDisabled && passesFilter(f),
          )
          return hasValidColumn
        }
        return fieldFilter(field)
      })
    } else if (allowedFieldTypes !== null && allowedFieldTypes !== undefined) {
      filteredFields = fields.filter(field => {
        if (field.isDisabled) {
          const hasValidColumn = fields.some(
            f =>
              f.parentFieldId === field.id && !f.isDisabled && passesFilter(f),
          )
          return hasValidColumn
        }
        if (field.id.startsWith('special_')) {
          if (allowedSpecialFieldIds === null) return true
          if (allowedSpecialFieldIds === undefined) return false
          if (allowedSpecialFieldIds.length === 0) return false
          return allowedSpecialFieldIds.includes(field.id)
        }
        return field.type !== null && allowedFieldTypes.includes(field.type)
      })
    } else {
      filteredFields = fields
    }

    this.cachedFilteredFields = filteredFields
    this.cacheKey = cacheKey
    return filteredFields
  }

  private getSelectableFieldsCountForStep(stepPosition: number): number {
    const { fields } = this.props
    const filteredFields = this.getFilteredFields()
    const filteredFieldIds = new Set(filteredFields.map(f => f.id))

    return fields.filter(
      field =>
        field.stepPosition === stepPosition &&
        !field.isDisabled &&
        !field.id.startsWith('special_') &&
        filteredFieldIds.has(field.id),
    ).length
  }

  private getFieldOptions(): IFieldOptionWithStep[] {
    const cacheKey = this.getCacheKey()
    if (
      this.cachedFieldOptions &&
      this.cacheKey === cacheKey &&
      this.collapsedSteps.size === 0
    ) {
      return this.cachedFieldOptions
    }

    const { permitType } = this.props
    const filteredFields = [...this.getFilteredFields()]

    filteredFields.sort((a, b) => {
      const aIsSpecial = a.id.startsWith('special_')
      const bIsSpecial = b.id.startsWith('special_')
      if (aIsSpecial && !bIsSpecial) return 1
      if (!aIsSpecial && bIsSpecial) return -1
      if (aIsSpecial && bIsSpecial) return 0

      if (b.parentFieldId === a.id && a.isDisabled) return -1
      if (a.parentFieldId === b.id && b.isDisabled) return 1

      if (a.stepPosition !== b.stepPosition) {
        return (a.stepPosition || 0) - (b.stepPosition || 0)
      }
      if (a.fieldPosition !== b.fieldPosition) {
        return (a.fieldPosition || 0) - (b.fieldPosition || 0)
      }
      if (a.subFieldPosition !== b.subFieldPosition) {
        return (a.subFieldPosition || 0) - (b.subFieldPosition || 0)
      }

      return 0
    })

    const fieldOptions: IFieldOptionWithStep[] = []
    const fieldsByStep = new Map<number, IWorkflowField[]>()
    const specialFields: IWorkflowField[] = []

    filteredFields.forEach(field => {
      if (field.id.startsWith('special_')) {
        specialFields.push(field)
      } else if (field.stepPosition) {
        if (!fieldsByStep.has(field.stepPosition)) {
          fieldsByStep.set(field.stepPosition, [])
        }
        const stepFields = fieldsByStep.get(field.stepPosition)
        if (stepFields) {
          stepFields.push(field)
        }
      }
    })

    const workflowSteps = permitType?.workflowSteps || []
    const stepMap = new Map<number, IWorkflowStep>()
    workflowSteps.forEach(
      (step: IWorkflowStep | null | undefined, index: number) => {
        if (step) {
          stepMap.set(index + 1, step)
        }
      },
    )

    const sortedStepPositions = Array.from(fieldsByStep.keys()).sort(
      (a, b) => a - b,
    )

    sortedStepPositions.forEach(stepPosition => {
      const stepFields = fieldsByStep.get(stepPosition)
      if (!stepFields) {
        return
      }

      const step = stepMap.get(stepPosition)
      let stepName: string
      if (step) {
        try {
          const stepLevel = step.id
            ? getWorkflowStepLevel(step.id, workflowSteps as IWorkflowStep[])
            : undefined
          stepName = getWorkflowStepDisplayName(step.type, stepLevel)
        } catch {
          stepName = getWorkflowStepDisplayName(step.type, undefined)
        }
      } else {
        stepName = `${Localization.translator.step} ${stepPosition}`
      }
      const isCollapsed = this.collapsedSteps.has(stepPosition)

      const selectableStepFieldsCount =
        this.getSelectableFieldsCountForStep(stepPosition)

      fieldOptions.push({
        isStepHeader: true,
        stepPosition,
        stepName,
        fieldCount: selectableStepFieldsCount,
      })

      if (!isCollapsed) {
        stepFields.forEach(field => {
          fieldOptions.push({
            field,
            isSelected:
              !field.isDisabled && this.safeSelectedFieldIds.includes(field.id),
          })
        })
      }
    })

    specialFields.forEach(field => {
      fieldOptions.push({
        field,
        isSelected:
          !field.isDisabled && this.safeSelectedFieldIds.includes(field.id),
      })
    })

    if (this.collapsedSteps.size === 0) {
      this.cachedFieldOptions = fieldOptions
    }

    return fieldOptions
  }

  @action.bound
  private toggleStepCollapse(stepPosition: number): void {
    if (this.collapsedSteps.has(stepPosition)) {
      this.collapsedSteps.delete(stepPosition)
    } else {
      this.collapsedSteps.add(stepPosition)
    }
    this.cachedFieldOptions = null
  }

  private hasAnySteps(): boolean {
    const { fields } = this.props
    return fields.some(
      field =>
        field.stepPosition !== undefined && !field.id.startsWith('special_'),
    )
  }

  private getStepHeaders(): IFieldOptionWithStep[] {
    const fieldOptions = this.getFieldOptions()
    return fieldOptions.filter(option => option.isStepHeader)
  }

  private areAllStepsExpanded(): boolean {
    const stepHeaders = this.getStepHeaders()
    if (stepHeaders.length === 0) return false
    return stepHeaders.every(header => {
      const { stepPosition } = header
      return (
        stepPosition !== undefined && !this.collapsedSteps.has(stepPosition)
      )
    })
  }

  private areAllStepsCollapsed(): boolean {
    const stepHeaders = this.getStepHeaders()
    if (stepHeaders.length === 0) return false
    return stepHeaders.every(header => {
      const { stepPosition } = header
      return stepPosition !== undefined && this.collapsedSteps.has(stepPosition)
    })
  }

  @action.bound
  private toggleAllStepsCollapse(): void {
    const stepHeaders = this.getStepHeaders()
    if (stepHeaders.length === 0) return

    const allExpanded = this.areAllStepsExpanded()

    if (allExpanded) {
      stepHeaders.forEach(header => {
        if (header.stepPosition !== undefined) {
          this.collapsedSteps.add(header.stepPosition)
        }
      })
    } else {
      this.collapsedSteps.clear()
    }
    this.cachedFieldOptions = null
  }

  private renderOption = ({
    key,
    style,
    index,
    fieldOptions,
  }: {
    key: string
    style: React.CSSProperties
    index: number
    fieldOptions: IFieldOptionWithStep[]
  }): JSX.Element | null => {
    const option = fieldOptions[index]

    if (!option) return null

    if (option.isStepHeader) {
      return this.renderStepHeader(key, style, option)
    }

    const { field, isSelected } = option
    if (!field) return null

    return this.renderFieldItem(key, style, field, isSelected)
  }

  private renderStepHeader(
    key: string,
    style: React.CSSProperties,
    option: IFieldOptionWithStep,
  ): JSX.Element {
    const { stepPosition, stepName, fieldCount } = option
    if (stepPosition === undefined) {
      return <div key={key} style={style} />
    }

    const isCollapsed = this.collapsedSteps.has(stepPosition)
    const { isMultiple, onSelectStepFields, onDeselectStepFields } = this.props

    const filteredFields = this.getFilteredFields()
    const stepFields = filteredFields.filter(
      f => f.stepPosition === stepPosition && !f.isDisabled,
    )
    const areAllStepFieldsSelected =
      stepFields.length > 0 &&
      stepFields.every(f => this.safeSelectedFieldIds.includes(f.id))
    const hasAnyStepFieldSelected = stepFields.some(f =>
      this.safeSelectedFieldIds.includes(f.id),
    )

    return (
      <div
        key={key}
        style={style}
        className={classList({
          'tw:flex tw:items-center bb-light-grey bg-palette-brand-lightest text-ellipsis tw:gap-2.5': true,
          pl17: !this.props.isMultiple,
          pl20: this.props.isMultiple,
        })}
      >
        {isMultiple && onSelectStepFields && onDeselectStepFields && (
          <div
            className="row x-center y-center mw14 mh14 ba-grey-scale-light brada2"
            onClick={e => {
              e.stopPropagation()
              if (areAllStepFieldsSelected) {
                onDeselectStepFields(stepPosition)
              } else {
                onSelectStepFields(stepPosition)
              }
            }}
          >
            {hasAnyStepFieldSelected && (
              <span className="text large bold brand-dark center">{DASH}</span>
            )}
          </div>
        )}
        <ChevronDownIcon
          className={classList({
            'tw:mx-1 tw:size-3': true,
            rotate180: !isCollapsed,
          })}
          onClick={() => this.toggleStepCollapse(stepPosition)}
        />
        <span className="text large">
          {Localization.translator.step} {stepPosition}: {stepName}
        </span>
        <span className="row text large medium-bold grey ml5">
          ({fieldCount})
        </span>
      </div>
    )
  }

  private renderFieldItem(
    key: string,
    style: React.CSSProperties,
    field: IWorkflowField,
    isSelected: boolean | undefined,
  ): JSX.Element {
    const { isMultiple, onFieldToggle, onFieldSelect } = this.props
    const {
      stepPosition,
      fieldPosition,
      subFieldPosition,
      shouldIndent,
      isConditional,
      isDisabled,
    } = field

    const positionString = [stepPosition, fieldPosition, subFieldPosition]
      .filter(p => p)
      .join('.')

    const handleClick = (): void => {
      if (isDisabled) return
      if (isMultiple && onFieldToggle) {
        onFieldToggle(field.id)
      } else if (onFieldSelect) {
        onFieldSelect(field.id)
      }
    }

    return (
      <div
        key={key}
        style={style}
        className={classList({
          'row y-center tw:gap-2.5 bb-light-grey px20 text-ellipsis field-item': true,
          selected: isSelected && !isDisabled,
        })}
        onClick={handleClick}
      >
        <div
          className={classList({
            'tw:flex tw:items-center tw:justify-center no-grow mw14 mh14 ba-grey-scale-light brada2': true,
            'ba-none': isDisabled,
            'bg-blue-40 ba-none': isSelected && !isDisabled,
          })}
          onClick={e => {
            e.stopPropagation()
            handleClick()
          }}
        >
          {isSelected && !isDisabled && (
            <CheckIcon className="tw:size-3 tw:text-primary-foreground" />
          )}
        </div>
        <span
          className={classList({
            'tw:flex tw:items-center text large no-grow': true,
            ml10: shouldIndent && !isConditional,
          })}
        >
          {isConditional ? (
            <CodeIcon className="tw:size-5 tw:text-muted-foreground" />
          ) : (
            positionString
          )}
        </span>
        <SafePermitFieldIcon fieldName={field.type} fieldId={field.id} />
        <span className="row text large ellipsis" title={field.caption}>
          {field.caption}
        </span>
      </div>
    )
  }

  private getSelectableFields(): IWorkflowField[] {
    const filteredFields = this.getFilteredFields()
    return filteredFields.filter(f => !f.isDisabled)
  }

  private areAllFieldsSelected(): boolean {
    const selectableFields = this.getSelectableFields()
    if (selectableFields.length === 0) return false
    return selectableFields.every(f => this.safeSelectedFieldIds.includes(f.id))
  }

  private hasAnyFieldSelected(): boolean {
    const selectableFields = this.getSelectableFields()
    if (selectableFields.length === 0) return false
    return selectableFields.some(f => this.safeSelectedFieldIds.includes(f.id))
  }

  private getTitle(): string {
    if (!this.safeSelectedFieldIds.length) {
      return Localization.translator.select || 'Select'
    }
    return `${this.safeSelectedFieldIds.length} ${
      Localization.translator.selected?.toLowerCase() || 'selected'
    }`
  }
}
